import React, { useState } from 'react';
import { formatDateTime, formatTimeShort } from 'utils/date.util';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Text, Flex, VStack, Box } from '@chakra-ui/react';
import { useColors } from './colors';

import { orderSupplierTaggingApi } from 'features/third-party-integrations/third-party-integrations.api';

const LastestUpdatesGraph = ({ timelineData = [] }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const { textColor, borderColor } = useColors();
  const dispatch = useDispatch();

  const { orderSupplierTaggingStatus } = useSelector(
    (state) => state.thirdPartyIntegrations,
  );

  const handleStartTagging = async () => {
    dispatch(orderSupplierTaggingApi());
  };

  const transformTimelineData = (timeline) => {
    if (!timeline || timeline.length === 0) {
      return [{ time: 'No Data', listings: 0, hour: 0 }];
    }

    return timeline
      .slice(0, 12)
      .reverse()
      .map((entry) => {
        const timeLabel = formatTimeShort(entry.timestamp);
        const hour = Number.parseInt(timeLabel.split(':')[0], 10) || 0;

        return {
          time: timeLabel,
          listings: entry.updatedOrdersCount,
          hour,
          fullDate: formatDateTime(entry.timestamp),
        };
      });
  };

  const cycleData = transformTimelineData(timelineData);

  const SimpleLineChart = ({ data, width = 1120, height = 200 }) => {
    const maxListings = Math.max(...data.map((d) => d.listings));
    const minListings = Math.min(...data.map((d) => d.listings));
    const range = maxListings - minListings || 1;

    const points = data.map((point, index) => {
      const x = (index / (data.length - 1)) * (width - 80) + 50;
      const y = height - 50 - ((point.listings - minListings) / range) * (height - 100);
      return { x, y, ...point };
    });

    const pathData = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');

    const areaPathData = `${pathData} L ${points[points.length - 1].x} ${height - 50} L ${points[0].x} ${height - 50} Z`;

    return (
      <Box position="relative" width="100%" height={height}>
        <svg
          width="100%"
          height={height}
          style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'var(--chakra-colors-gray-200)',
          }}
          onMouseLeave={() => setHoveredPoint(null)}
        >
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path
                d="M 20 0 L 0 0 0 20"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="0.5"
                opacity="0.5"
              />
            </pattern>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#4299e1" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#4299e1" stopOpacity="0.05" />
            </linearGradient>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#2b6cb0" />
              <stop offset="50%" stopColor="#4299e1" />
              <stop offset="100%" stopColor="#63b3ed" />
            </linearGradient>
          </defs>

          <rect width="100%" height="100%" fill="url(#grid)" opacity="0.3" />

          {[0, 25, 50, 75, 100].map((percent) => {
            const value = Math.round(minListings + (percent / 100) * range);
            const y = height - 50 - (percent / 100) * (height - 100);
            return (
              <g key={percent}>
                <line
                  x1={45}
                  y1={y}
                  x2={width - 20}
                  y2={y}
                  stroke="#e2e8f0"
                  strokeWidth={1}
                  strokeDasharray="2,2"
                />
                <text x={35} y={y + 4} fontSize="10" fill="#64748b" textAnchor="end">
                  {value}
                </text>
              </g>
            );
          })}

          {points.map((point, index) => {
            if (index % 2 === 0) {
              return (
                <text
                  key={index}
                  x={point.x}
                  y={height - 25}
                  fontSize="10"
                  fill="#64748b"
                  textAnchor="middle"
                >
                  {point.time}
                </text>
              );
            }
            return null;
          })}

          <path d={areaPathData} fill="url(#areaGradient)" />

          <path
            d={pathData}
            fill="none"
            stroke="url(#lineGradient)"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {points.map((point, index) => (
            <g key={index}>
              <circle
                cx={point.x}
                cy={point.y}
                r={6}
                fill="white"
                stroke="#4299e1"
                strokeWidth={3}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredPoint(point)}
                onMouseLeave={() => setHoveredPoint(null)}
              />
              <circle
                cx={point.x}
                cy={point.y}
                r={3}
                fill="#4299e1"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredPoint(point)}
                onMouseLeave={() => setHoveredPoint(null)}
              />
            </g>
          ))}

          <text
            x={width / 2}
            y={25}
            fontSize="13"
            fontWeight="600"
            fill="#64748b"
            textAnchor="middle"
          >
            Recent tagging activity
          </text>
        </svg>

        {hoveredPoint && (
          <Box
            position="absolute"
            left={hoveredPoint.x}
            top={hoveredPoint.y < 120 ? hoveredPoint.y : hoveredPoint.y - 100}
            transform="translateX(-50%)"
            bg="rgba(0,0,0,0.9)"
            color="white"
            fontSize="sm"
            p={3}
            borderRadius="lg"
            zIndex={10}
            minWidth="180px"
          >
            <VStack spacing={1} align="center">
              <Text fontWeight="bold" color="#63b3ed">
                {hoveredPoint.fullDate || hoveredPoint.time}
              </Text>
              <Text fontSize="xs" color="gray.300">
                Orders tagged
              </Text>
              <Text fontSize="lg" fontWeight="bold">
                {hoveredPoint.listings.toLocaleString()}
              </Text>
            </VStack>
          </Box>
        )}
      </Box>
    );
  };

  const lastRunLabel =
    timelineData.length > 0 ? formatDateTime(timelineData[0]?.timestamp) : null;

  return (
    <VStack align="stretch" spacing={5} width="100%">
      <Flex
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        flexDirection={{ base: 'column', md: 'row' }}
        gap={4}
      >
        {lastRunLabel && (
          <Text fontSize="sm" color="gray.500">
            Last manual run: {lastRunLabel}
          </Text>
        )}
        <Button
          colorScheme="blue"
          size="md"
          onClick={handleStartTagging}
          variant="outline"
          alignSelf={{ base: 'stretch', md: 'flex-end' }}
          isLoading={orderSupplierTaggingStatus === 'pending'}
          loadingText="Starting…"
          isDisabled={orderSupplierTaggingStatus === 'pending'}
        >
          Start manual tagging
        </Button>
      </Flex>

      <Box
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        overflow="hidden"
        p={2}
      >
        <SimpleLineChart data={cycleData} height={220} />
      </Box>
    </VStack>
  );
};

export default LastestUpdatesGraph;
